import React, { useContext, useEffect, useRef, useState } from "react";
import { formEdit, getMulticheckIconForTree } from "../../../Tools/Tools";
import ContextMenu from "../../../NotWorkArea(Side&Head)/ContextMenu";
import LoadingMask from "../../../NotWorkArea(Side&Head)/LoadingMask";
import "./treeClasses.css";
import { createFullGroupItem, createFullItem } from "./treeComponents/treeCreationTools";
import {
  createContextGroup,
  createContextItem,
  deleteContextItem,
  getTreeView,
  openModalParams,
  treeManipulationsById,
} from "./treeComponents/treeContextMenuTools";
import TreeSearch from "./treeComponents/TreeSearch";
import { SmetaModalsButtonsContext } from "../../../Tools/modalManager";
import styles from "./treeStyles.module.css";
import { HTMLTreeUListElement } from "../../../../ComponentInterface";
import smetaAxios from "../../../../SmetaAxios";
import { Button as DlgButton } from "../../../Tools/ProjectX";

function getNextState(arr: number[], state: string) {
  const length = arr.length;
  const index = arr.indexOf(Number(state));

  return index < length - 1 ? arr[index + 1] : arr[0];
}

interface GetGroupTreeItem {
  id?: string;
  ID?: string;
  text?: string;
  Text?: string;
  leaf: boolean;
  Code?: string;
  image?: string;
  Image?: string;
  items?: GetGroupTreeItem[];
}

interface GetGroupTreeRes {
  Current?: string;
  Tree: {
    items: GetGroupTreeItem[];
  };
}

interface TreeProps {
  CheckSet?: number[];
  showButtons?: boolean;
  buttons?: string | number;
  saveState?: boolean;
  searchVisible?: boolean;
  onSelect?: (tree: HTMLTreeUListElement | null, data: TreeProps, selection: string) => Promise<void> | void;
  ObjRef?: string | number;
  Selection?: string;
  noExpandFirst?: boolean;
  defaultData?: any;
  Current?: string | number;
  MultiSel?: boolean;
  defaultMultiSel?: boolean;
  ref?: React.RefObject<HTMLTreeUListElement | null>;
  checkModeOnly?: boolean;
  selectID?: (id: string) => void;
  selectItem?: any;
  setSelectionMode?: (selectionModeCurrent: boolean) => void;
  paramsCustomClick?: any;
  Module?: string;
  defaultMenu?: string[];
  Info?: string | number;
  ObjType?: string | number;
  CLSID?: string;
  Path?: string;
  RequestID?: string;
  func?: any;
  from?: any;
  onEdit?: any;
  setValue?: any;
  setInputValue?: any;
  Name?: string;
  record?: any;
  addInfo?: any;
  itemData?: any;
  customReq?: (data: any) => Promise<GetGroupTreeRes>;
  SectionID?: string | number;
  UsedDate?: string | number;
  Text?: string;
  MultiCheckSet?: boolean;
}

interface AddItemsData {
  treeItems: Array<Record<string, any>>;
  currentHtml: "tree" | Element | null;
  currentData: { Current?: string };
  parent?: Record<string, any>;
}

/**
 * @param props - Параметры компонента.
 * @param props.ref - Переменная хука useRef() в которую будет записана ссылка на компонент дерева.
 * @param props.selectID - function(id){setID(id)} *useState()* - функция setState() для получения выбранного id элемента/массива выбранных id элементов.
 * @param props.Current - Изначально выбранный элемент при первой отрисовке.
 * @param props.Selection - Изначальные id элементов выбранных множественным выбором через запятую.
 * @param {boolean} props.checkModeOnly - Только множественный выбор.
 * @param {boolean} props.defaultMultiSel - Множественный выбор по умолчанию.
 * @param {boolean} props.MultiSel - Режим возможности переключаться между множественным и одиночным выбором.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
const Tree = ({ props }: { props: TreeProps }) => {
  //хук для хранения id группы, можно получить передав по пропсам setState под ключом selectID
  const [groupID, setGroupID] = useState<string | string[]>();
  const itemsMap = useRef<Record<string, { Code: string; id: string; leaf: boolean; parent?: any; text: string }>>({});
  //хук для хранения данных последнего запроса параметров дерева
  const [load, setLoad] = useState(true);
  // переменная для первой загрузки
  const firstLoad = useRef(true);
  // главный контейнер
  const treeContainer = useRef<HTMLDivElement>(null);

  const reqData = useRef({ Keys: "", ...props });

  // переменная с данными по отрисованному дереву
  const values = useRef<Record<string, string>>({});
  // переменные для множественного выбора
  const selection = useRef<string[]>([]);
  const selectionMode = useRef(false);
  //элемент дерево
  const tree = useRef<HTMLTreeUListElement | null>(null);

  const treeJson = useRef({});
  const fullJson = useRef<GetGroupTreeRes>(undefined);

  const checkSet = useRef<number[]>(props.CheckSet || []);

  const setButtons = useContext(SmetaModalsButtonsContext);
  const showSelectionPanel = props.MultiSel && !props.checkModeOnly;
  const isSelectionChecked =
    props.checkModeOnly || (props.MultiSel && (Boolean(props.Selection) || props.defaultMultiSel));

  useEffect(() => {
    if (!props.showButtons) return;
    const modalButtons: { [key: number]: { label: string; onClick?: () => void } } = {};
    if (!props.buttons || Number(props.buttons) & DlgButton.Ok) {
      modalButtons[1] = {
        label: "Выбрать",
        onClick: () => {
          if (props.onSelect) {
            props.onSelect(tree.current, props, selection.current.join(","));
            return;
          }
          onSelect();
        },
      };
    }
    setButtons?.(modalButtons);
  }, [setButtons, tree.current, selectionMode.current, values.current]);

  // при первой отрисовке заполняем дерево элементами
  useEffect(() => {
    if (tree.current) {
      tree.current.addGroupID = addGroupID;
      tree.current.selectGroupID = selectGroupID;
      tree.current.updateMultiCheckState = updateMultiCheckState;
      tree.current.changeSelected = handleChangeSelected;
      tree.current.refreshTree = refreshTree;
    }
    if (props.Selection) {
      selection.current = props.Selection.split(",");
    }

    // первая отрисовка
    if (tree.current) {
      if (props.defaultData) {
        tree.current.innerHTML = "";
        addNewItems({
          treeItems: props.defaultData["Tree"]["items"],
          currentHtml: "tree",
          currentData: props.defaultData,
        });

        if (!props.noExpandFirst) expandItem();
        setLoad(false);
        return;
      }

      getGroupTree(undefined, undefined, reqData.current).then((res) => {
        if (!res) return;

        if (props.Current === undefined) {
          getGroupTree(undefined, res.Current, reqData.current).then(() => {
            if (tree.current) tree.current.innerHTML = "";
            if ((props.MultiSel && props.Selection) || (props.MultiSel && props.defaultMultiSel))
              selectionMode.current = true;
            addNewItems({
              treeItems: res["Tree"]["items"],
              currentHtml: "tree",
              currentData: {
                Current: res.Current,
              },
            });
            // разворачивание первого выбранного элемента
            if (!props.noExpandFirst) expandItem();
            setLoad(false);
          });
        } else {
          if (tree.current) tree.current.innerHTML = "";
          if ((props.MultiSel && props.Selection) || (props.MultiSel && props.defaultMultiSel))
            selectionMode.current = true;
          addNewItems({ treeItems: res["Tree"]["items"], currentHtml: "tree", currentData: { Current: res.Current } });
          // разворачивание первого выбранного элемента
          if (!props.noExpandFirst) expandItem();
          setLoad(false);
        }

        if (props.saveState) {
          fillRowObject({ id: "tree", items: res.Tree.items }, treeJson.current);
          fullJson.current = res;
        }
      });
    }
  }, []);

  // при изменении id выбранной группы вызывается setState переданный по пропсам для передачи данных в родительский компонент
  useEffect(() => {
    if ((props.selectID || props.selectItem) && groupID !== undefined) {
      if (selectionMode.current && typeof groupID === "object") {
        props.selectID?.(groupID.join(","));
      } else {
        props.selectID?.(groupID);
        props.selectItem?.(itemsMap.current[groupID]);
        reqData.current.itemData = itemsMap.current[groupID];
      }
    }
  }, [groupID]);

  useEffect(() => {
    if (props.setSelectionMode) {
      props.setSelectionMode(selectionMode.current);
    }
  }, [selectionMode.current]);

  // внешние функции
  useEffect(() => {
    if (tree.current) {
      // информация по текущему элементу
      tree.current.getCurrentInfo = getCurrentInfo;
      // информация по элементу с переданным id
      tree.current.getSelectData = getSelectData;
    }
  }, [tree.current]);

  // Запрос дерева. Для запроса необходимо передать только необходимые параметры
  function getGroupTree(idItem: string | undefined, current: string | string[] | number | undefined, data: TreeProps) {
    if (data.customReq) {
      return data.customReq({
        ...data,
        Current: current || data.Current,
        GroupId: idItem,
        itemData: itemsMap.current[idItem],
      });
    }
    // возвращаем Promise
    return smetaAxios.get<GetGroupTreeRes>("GetGroupTree", {
      prefix: data.Module,
      params: {
        SectionID: data.SectionID || undefined,
        Info: data.Info || undefined,
        UsedDate: data.UsedDate || undefined,
        ObjType: data.ObjType || undefined,
        Current: idItem || current || data.Current || undefined,
        ObjRef: data.ObjRef || undefined,
        Text: data.Text || undefined,
        GroupID: idItem || undefined,
        Path: !data.CLSID && data.Path ? data.Path : undefined,
      },
      loadMask: true,
    });
  }

  // добавление элементов в дерево
  function addNewItems({ treeItems, currentHtml, currentData, parent }: AddItemsData) {
    if (!treeItems || !tree.current) return;

    // перебор элементов массива с данными
    const items = treeItems;

    if (!currentData.Current && items.length) {
      currentData.Current = items[0].id;
    }

    items.forEach((item, index) => {
      const id = item.id ? item.id : item.ID;
      itemsMap.current[id] = { ...item, parent };
      // установка первого элемента как выбранный если он не указан в ответе
      if (firstLoad.current && (currentData.Current === id || !currentData.Current)) {
        if (selectionMode.current && selection.current.length) {
          setGroupID([...selection.current]);
        } else {
          setGroupID(id);
        }
        firstLoad.current = false;
      }

      // текст и картинка из запроса
      const text = item.text || item.Text;
      const image = item.image || item.Image;

      const data = {
        text,
        res: currentData,
        id,
        item,
        items,
        index,
        image,
        props,
        selection,
        selectionMode,
        values,
      };

      // если это законченная ветвь, то создаем элемент tree-item внутри элемента с id current
      if (item.leaf) {
        const fullItem = createFullItem(data);

        // Добавление элемента в ДОМ
        if (currentHtml === "tree" || !currentHtml) {
          tree.current?.appendChild(fullItem);
        } else {
          currentHtml.appendChild(fullItem);
        }

        // обновление выбранной группы если id совпадает с ObjRef или Current
        if (id === props.ObjRef || id === currentData.Current) {
          if (selectionMode.current && selection.current.length) {
            setGroupID([...selection.current]);
          } else {
            setGroupID(id);
          }
        }
      } else {
        const fullGroupItem = createFullGroupItem(data);

        // Добавление элемента в ДОМ
        if (currentHtml === "tree" || !currentHtml) {
          tree.current?.appendChild(fullGroupItem);
        } else {
          currentHtml.appendChild(fullGroupItem);
        }

        // обновление выбранной группы если id совпадает с ObjRef или Current
        if (id === props.ObjRef || id === currentData.Current) {
          if (selectionMode.current && selection.current.length) {
            setGroupID([...selection.current]);
          } else {
            setGroupID(id);
          }
        }

        // вызов рекурсии если группа содержит внутри items
        // как current передается текущий элемент дерева к которому будут добавляться новые ветви (LI)
        if (item.items)
          addNewItems({
            treeItems: item.items,
            currentHtml: fullGroupItem.querySelector("LI"),
            currentData,
            parent: item,
          });
      }
    });
  }

  function fillRowObject(inputObj: { id: string; items?: GetGroupTreeItem[] }, resultObj: any) {
    if (inputObj.items) {
      for (const elem of inputObj.items) {
        fillRowObject(elem, resultObj);
      }
    }

    resultObj[inputObj.id] = inputObj;
  }

  async function addGroupID(id: string) {
    if (selectionMode.current) {
      let item = tree.current?.querySelector<HTMLDivElement>(`#text-tree-item_${id}`);

      if (!item) {
        if (selection.current.indexOf(id) === -1) {
          selection.current.push(id);
          setGroupID([...selection.current]);
        }
        await refreshTree(id);
      }

      if (item) item = item.closest<HTMLDivElement>(".tree-item");
      if (!item) return;

      const checkbox = item.querySelector<HTMLInputElement>(`input[type="checkbox"]`);

      if (checkbox && !checkbox.checked) {
        checkbox.click();
      }
    }
  }

  async function selectGroupID(id: string | number) {
    if (!selectionMode.current) {
      let item = tree.current?.querySelector<HTMLDivElement>(`#text-tree-item_${id}`);
      if (!item) await refreshTree(id);
      if (item) item = item.closest<HTMLDivElement>(".tree-item");
      if (!item) return false;
      item.click();
      return true;
    }
    return false;
  }

  function getSelectData() {
    const item = tree.current?.querySelector<HTMLDivElement>(".tree-item-selected");
    let id = "";
    let itemText: string | undefined;
    if (item) {
      id = item.id;
      itemText = tree.current?.querySelector<HTMLDivElement>(`#text-tree-item_${id}`)?.innerText;
    }
    if (itemText && id) return { label: itemText, id: id };
    return {};
  }

  function getCurrentInfo() {
    const item =
      tree.current?.querySelector<HTMLDivElement>(".tree-item-selected") ||
      tree.current?.querySelector<HTMLDivElement>(".tree-item");

    return {
      GroupID: item && item.id,
      item: item,
      title: item && item.querySelector<HTMLDivElement>(`#text-tree-item_${item.id}`)?.innerText,
      selectionMode: selectionMode.current,
      selection: selection.current,
    };
  }

  // функция для разворачивания дерева по id группы
  async function getNewLeaf(current: Element, id: string) {
    const res = await getGroupTree(id, undefined, reqData.current);
    addNewItems({ treeItems: res["Tree"]["items"], currentHtml: current, currentData: { Current: res.Current } });
    if (props.saveState) {
      treeJson.current[res.Current]["items"] = res.Tree.items;
      fillRowObject({ id: "lastLeaf", items: res.Tree.items }, treeJson.current);
    }
  }

  async function contextMenuHandler(data: { value: string; item?: HTMLElement }) {
    const command = data.value;
    const parent = (data.item && data.item.closest<HTMLLIElement>("LI")) || tree.current;
    const item = data.item;

    const createData = {
      res: { Current: groupID },
      item: {
        noDel: item && item.dataset.noDel,
        NoEdit: item && item.dataset.noEdit,
      },
      items: [],
      index: 0,
      props,
      selection,
      selectionMode,
      values,
    };
    const properties = { ...data, parent, createData, props, tree };
    const commands: Record<string, () => Promise<void> | void> = {
      AddSection: () => createContextItem(properties),
      AddSubSection: () => createContextGroup(properties),
      Delete: () => deleteContextItem(properties),
      Params: () => {
        if (props.paramsCustomClick) {
          props.paramsCustomClick({ groupId: groupID, module: props.Module });
        } else openModalParams({ GroupID: groupID, Module: props.Module });
      },
      default: () => {
        treeManipulationsById(properties);
      },
    };
    const doCommand = commands[command] || commands.default;
    await doCommand();
  }

  // ВРЕМЕННЫЙ КОСТЫЛЬ требуется доработка сервера
  function addItemsForChecking(
    input: GetGroupTreeItem[],
    current: HTMLLIElement | null,
    res: GetGroupTreeRes,
    arr: HTMLElement[],
  ) {
    if (!input || !tree.current) return;

    const items = input;

    items.forEach((item, index) => {
      const id = item.id ? item.id : item.ID!;

      const text = item.text ? item.text : item.Text!;
      const image = item.image ? item.image : item.Image!;
      const data = {
        text,
        res,
        id,
        item,
        items,
        index,
        image,
        props,
        selection,
        selectionMode,
        values,
      };
      if (item.leaf) {
        const ul = createFullItem(data);
        arr.push(ul);
        if (current === "tree") {
          tree.current?.appendChild(ul);
        } else {
          current.appendChild(ul);
        }
      } else {
        const ul = createFullGroupItem(data);
        arr.push(ul);
        if (current === "tree") {
          tree.current?.appendChild(ul);
        } else {
          current.appendChild(ul);
        }
        if (item.items) addItemsForChecking(item.items, null, res, arr);
      }
    });
  }

  // ВРЕМЕННЫЙ КОСТЫЛЬ=
  async function loadAllInnerGroups(parentGroup: HTMLUListElement) {
    const li = parentGroup.querySelector<HTMLLIElement>("li");
    const children = li.children;
    if (children.length === 0) {
      const item = parentGroup.querySelector<HTMLDivElement>(".tree-item");
      const id = item.id;
      const res = await getGroupTree(id, groupID, reqData.current);
      li.style.display = "none";
      const children: HTMLUListElement[] = [];
      addItemsForChecking(res["Tree"]["items"], li, res, children);
      for (const child of children) {
        if (child.group) {
          await loadAllInnerGroups(child);
        }
      }
    } else {
      for (const child of children) {
        if (child.group) {
          await loadAllInnerGroups(child);
        }
      }
    }
    return "";
  }

  async function onCheckGroup(data: { check: boolean; parentElement: HTMLUListElement }) {
    // ВРЕМЕННЫЙ КОСТЫЛЬ
    await loadAllInnerGroups(data.parentElement);
    // выбор всей группы
    const items = data.parentElement.querySelectorAll(`ul`);
    // выбор для внешних элементов
    checking(items, data);
  }

  // ВРЕМЕННЫЙ КОСТЫЛЬ
  async function checking(
    items: NodeListOf<HTMLUListElement>,
    data: {
      check: boolean;
      parentElement: HTMLUListElement;
    },
  ) {
    items.forEach((item) => {
      const div = item.querySelector<HTMLDivElement>(".tree-item");
      const id = div.id;
      const checkbox = div.querySelector<HTMLInputElement>(`input[type="checkbox"]`);

      checkbox.checked = data.check;
      updateSelection(checkbox.checked, id);
    });
    setGroupID([...selection.current]);
    setLoad(false);
  }

  // функция для распахивания по элементу
  function expandItem() {
    if (!tree.current) return;
    const selected = tree.current.querySelector<HTMLDivElement>(".tree-item-selected");
    if (selected) {
      const bttn = selected.querySelector<HTMLButtonElement>(".expandButton");
      if (bttn) bttn.click();
    }
  }

  // функция смены выбранного элемента визуально
  function changeSelected(item?: HTMLDivElement) {
    if (!item) return;
    const oldSelectedItem = tree.current?.querySelector<HTMLDivElement>(".tree-item-selected");

    if (oldSelectedItem) {
      if (oldSelectedItem === item) return;
      oldSelectedItem.classList.remove("tree-item-selected");
    }

    item.classList.add("tree-item-selected");
  }

  async function refreshTree(id: string | number, subData: Record<string, any> = {}) {
    reqData.current = { ...reqData.current, ...subData };
    if (tree.current) tree.current.innerHTML = "";
    const json = await getGroupTree(undefined, id, reqData.current);
    addNewItems({ treeItems: json["Tree"]["items"], currentHtml: "tree", currentData: { Current: json.Current } });
    await selectGroupID(json.Current || id);
    setLoad(false);

    if (props.saveState) {
      fillRowObject({ id: "tree", items: json.Tree.items }, treeJson.current);
      fullJson.current = json;
    }

    values.current = {};
  }

  function handleChangeSelected(item: HTMLDivElement) {
    if (!item) return;
    changeSelected(item);
    setGroupID(item.id);
  }

  // функция обновление массива выбранных элементов
  function updateSelection(checked: boolean, id: string) {
    if (checked) {
      selection.current.push(id);
    } else {
      selection.current = selection.current.filter((item) => item !== id);
    }
  }

  // поворот кнопки в зависимости от состояния распахивания
  function updateStateExpandButton(bttn: HTMLButtonElement, state: boolean) {
    bttn.expanded = state ? "true" : "false";
    bttn.style.transform = state ? "" : "rotate(-90deg)";
  }

  // функция клика на кнопку распахивания
  const onClickExpandButton = (ev: React.MouseEvent) => {
    const target = ev.target as HTMLElement;
    const bttn = target.closest<HTMLButtonElement>(".expandButton");

    updateStateExpandButton(bttn, bttn.expanded === "false");

    const el = target.closest("ul");

    if (el && el.group === "true") {
      if (bttn.expanded === "true") {
        if (el.children[1].children.length === 0) {
          getNewLeaf(el.children[1], el.children[1].dataset.id);
        } else {
          el.children[1].style.display = "";
        }
        el.expanded = "true";
      } else {
        el.children[1].style.display = "none";
        el.expanded = "false";
      }
    }
  };

  // функция клика по чекбоксу
  function onClickCheckBox(ev: React.MouseEvent) {
    const target = ev.target as HTMLInputElement;
    const checkbox = target.tagName === "INPUT" && target.type === "checkbox" ? target : null;
    if (checkbox) {
      const treeItem = checkbox.closest<HTMLDivElement>(".tree-item");
      const id = treeItem.id;
      const ulGroup = checkbox.closest<HTMLUListElement>("UL");
      updateSelection(checkbox.checked, id);

      if (ulGroup && ulGroup.group) {
        onCheckGroup({ check: checkbox.checked, parentElement: ulGroup });
        return;
      }
      setGroupID([...selection.current]);
    }
  }

  async function changeNodeState(current: string, state: number) {
    return smetaAxios.get("ChangeNodeState", {
      prefix: reqData.current.Module,
      params: {
        Info: reqData.current.Info,
        ObjType: reqData.current.ObjType,
        Path: !reqData.current.CLSID && reqData.current.Path ? reqData.current.Path : undefined,
        Current: current,
        State: state,
      },
      loadMask: true,
    });
  }

  async function reviewTreeNodes(data: { Info?: string | number; Keys: string }) {
    return smetaAxios.get<{
      items: {
        ID: string;
        Image: string;
        CheckState: string;
      }[];
    }>("ReviewTreeNodes", {
      prefix: reqData.current.Module,
      params: {
        Info: data.Info ? data.Info : reqData.current.Info,
        ObjType: reqData.current.ObjType,
        Path: !reqData.current.CLSID && reqData.current.Path ? reqData.current.Path : undefined,
        Keys: data.Keys,
      },
      loadMask: true,
    });
  }

  async function updateMultiCheckState(id: string | number, subData: Record<string, any> = {}) {
    const keys = [];
    const collection = tree.current?.querySelectorAll<HTMLDivElement>(".tree-item-checkbox-mc");
    if (collection)
      for (const checkbox of collection) {
        keys.push(checkbox.id.split("_")[1]);
      }
    reqData.current = { ...reqData.current, Info: id, Keys: keys.join(","), ...subData };
    const json = await reviewTreeNodes({ Info: id, Keys: keys.join(",") });
    if (json && json.items && json.items.length) {
      json.items.forEach((item) => {
        const innerCheckBox = tree.current?.querySelector(`div[id="mc_${item.ID}"]`);
        if (innerCheckBox) {
          innerCheckBox.checkState = item.CheckState;
          innerCheckBox.innerHTML = getMulticheckIconForTree(item.CheckState);
        }
      });
    }
  }

  function getKeysForReq(parentElement: HTMLUListElement, resArr: string[]) {
    const children = parentElement.children;
    resArr.push(children[0].id);
    if (children[1].children.length) {
      for (const child of children[1].children) {
        getKeysForReq(child, resArr);
      }
    }
  }

  async function onClickCheckBoxMC(ev: React.MouseEvent) {
    const checkbox = (ev.target as HTMLElement).closest<HTMLDivElement>(".tree-item-checkbox-mc");
    const parentElement = checkbox?.closest<HTMLUListElement>("UL");
    if (checkbox) {
      const nextState = getNextState(checkSet.current, checkbox.checkState);
      checkbox.checkState = nextState;
      checkbox.innerHTML = getMulticheckIconForTree(nextState);
      await changeNodeState(checkbox.id.split("_")[1], nextState);
      const keys: string[] = [];
      getKeysForReq(parentElement, keys);
      const json = await reviewTreeNodes({ Keys: keys.join(",") });
      if (json && json.items && json.items.length) {
        json.items.forEach((item) => {
          const innerCheckBox = parentElement?.querySelector<HTMLDivElement>(`div[id="mc_${item.ID}"]`);
          if (innerCheckBox) {
            innerCheckBox.checkState = item.CheckState;
            innerCheckBox.innerHTML = getMulticheckIconForTree(item.CheckState);
          }
        });
      }
    }
  }

  // функция на клик по дереву
  async function onClickItem(ev: React.MouseEvent) {
    const target = ev.target as HTMLElement;
    // клик по кнопке распахивания
    if (target.closest(".expandButton")) {
      onClickExpandButton(ev);
      return;
    }
    // клик по чекбоксу
    if (target.closest(".tree-item-checkbox")) {
      onClickCheckBox(ev);
      return;
    }
    // клик по чекбоксу с множеством состояний
    if (target.closest(".tree-item-checkbox-mc")) {
      await onClickCheckBoxMC(ev);
      return;
    }

    const item = target.closest<HTMLDivElement>(".tree-item");
    if (!item) return;
    // визуальный выбор нового элемента
    changeSelected(item);

    // выбор для внешних элементов
    if (!selectionMode.current) {
      setGroupID(item.id);
    }
  }

  //функция на двойной клик по дереву
  function onDoubleClick(ev: React.MouseEvent<HTMLUListElement>) {
    const target = ev.target as HTMLElement;
    if (target.closest(".expandButton")) return;
    if (target.closest(".tree-item-checkbox")) return;
    if (target.closest(".tree-item-checkbox-mc")) return;

    const el = target.closest<HTMLUListElement>("ul");
    const expandButton = el?.querySelector<HTMLButtonElement>(".expandButton");
    if (expandButton) expandButton.click();
  }

  // функция на клик по чек боксу множественного выбора
  function onChangeSelectionMode(ev: React.ChangeEvent<HTMLInputElement>) {
    const checkboxes = tree.current?.querySelectorAll<HTMLInputElement>(".tree-item-checkbox");
    if (!checkboxes) return;

    if (ev.target.checked) {
      selectionMode.current = true;
      checkboxes.forEach((item) => {
        item.style.display = "";
      });
      setGroupID([...selection.current]);
    } else {
      selectionMode.current = false;
      checkboxes.forEach((item) => {
        item.style.display = "none";
      });
      const itemSelected = tree.current?.querySelector<HTMLDivElement>(".tree-item-selected");
      setGroupID(itemSelected?.id);
    }
  }

  // кнопка выбрать
  async function onSelect() {
    const treeItemSelected = tree.current?.querySelector<HTMLDivElement>(".tree-item-selected");
    if (!treeItemSelected) return;

    if (props.RequestID) {
      await formEdit(
        "1",
        props.func,
        {
          Name: "ObjRef",
          Value: treeItemSelected.id,
        },
        props.RequestID,
        props.from,
      );
      return;
    }

    if (props.onEdit && props.setValue && props.setInputValue) {
      const value = values.current[treeItemSelected.id];

      props.onEdit({
        value: value,
        tag: treeItemSelected.id,
        name: props.Name,
        record: props.record,
        textchanged: "0",
        setValue: props.setValue,
        setInputValue: props.setInputValue,
        type: props.Module,
        requestId: props.RequestID,
        addInfo: props.addInfo,
        multiselect: selectionMode.current ? "1" : "0",
        selection: selectionMode.current ? selection.current.join(",") : undefined,
      });
      return;
    }
  }

  const onKeyDown = (ev: React.KeyboardEvent<HTMLDivElement>) => {
    if ((ev.target as HTMLElement).tagName === "INPUT") return;
    ev.preventDefault();
    const selected = treeContainer.current?.querySelector<HTMLDivElement>(".tree-item-selected");
    const ul = selected?.parentElement as HTMLUListElement | undefined | null;
    const expanded = ul?.expanded;

    const keys: Partial<
      Record<string, (selected: HTMLDivElement, expanded?: string, ul?: HTMLUListElement | null) => void>
    > = {
      ArrowRight: switchCollapse,
      ArrowLeft: switchCollapse,
      ArrowUp: selectPrev,
      ArrowDown: selectNext,
    };

    keys[ev.code]?.(selected, expanded, ul);
  };

  const switchCollapse = (selected: HTMLDivElement) => {
    const button = selected.querySelector<HTMLButtonElement>(".expandButton");
    button?.click();
  };

  const selectNext = (selected: HTMLDivElement, expanded: string | undefined, ul?: HTMLUListElement | null) => {
    if (!ul) return;
    if (expanded === "true") {
      const li = ul.querySelector("LI");
      selectTreeItem(li?.firstChild);
    } else {
      const next = ul.nextElementSibling;
      if (next) {
        selectTreeItem(next);
      } else {
        selectNext(selected, undefined, ul.closest<HTMLLIElement>("LI")?.parentElement);
      }
    }
  };
  const selectPrev = (_selected: HTMLDivElement, _expanded: string | undefined, ul?: HTMLUListElement | null) => {
    if (!ul) return;
    const prev = ul.previousElementSibling as HTMLUListElement | null;
    if (prev) {
      if (prev.expanded === "true") {
        const li = prev.querySelector<HTMLLIElement>("LI");
        selectTreeItem(li?.lastChild);
        return;
      }
      selectTreeItem(prev);
    } else {
      const group = ul.parentElement?.closest<HTMLUListElement>("UL");
      selectTreeItem(group);
    }
  };

  const selectTreeItem = (el?: HTMLUListElement | null) => {
    if (!el) return;
    const treeItem = el.querySelector<HTMLDivElement>(".tree-item");
    treeItem?.click();
    treeItem?.focus();
  };

  return (
    <div onKeyDown={onKeyDown} ref={treeContainer} className={styles.treeContainer}>
      {load && <LoadingMask />}
      <TreeSearch
        visible={props.searchVisible}
        onEdit={(val) => {
          const firstId = treeContainer.current?.querySelector(".tree-item");
          if (firstId) refreshTree(firstId.id, { Text: val });
        }}
      />
      <div className={styles.content}>
        <ContextMenu
          for={"tree"}
          onRightClick={(id: string) => getTreeView(id, props)}
          defaultMenu={props.defaultMenu || ["add", "addSub", "del", "params"]}
          onMenuItemClick={contextMenuHandler}
        >
          <div className={styles.viewport}>
            <ul
              className={styles.treeList}
              id={"tree"}
              onClick={onClickItem}
              onDoubleClick={onDoubleClick}
              ref={(ref) => {
                tree.current = ref;
                if (props.ref) props.ref.current = ref;
              }}
            />
          </div>
        </ContextMenu>
      </div>
      {showSelectionPanel && (
        <div className={styles.selectionPanel}>
          <input
            className={styles.selectionCheckbox}
            defaultChecked={isSelectionChecked}
            type="checkbox"
            onChange={onChangeSelectionMode}
          />
          <div className={styles.selectionLabel}>Множественный выбор</div>
        </div>
      )}
    </div>
  );
};

export default Tree;
